import { subDays } from "date-fns/subDays";
import { startOfToday } from "date-fns/startOfToday";
import { TriangleAlert } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact } from "../types";
import { useDashboardScope } from "./useDashboardScope";

/** Rows rendered before the list stops being a shortlist. */
const MAX_ROWS = 8;

/** How long an engaged contact may go untouched before it is an exception. */
const STALE_CONTACT_DAYS = 14;

/**
 * Ceiling on the company window scanned for the "open deal, no next action"
 * exception. `nb_open_tasks` and `open_deals_amount` are rollup columns of the
 * `companies_summary` view, which the FakeRest provider derives on read — so
 * neither can be filtered or sorted server-side, and the exception is found in
 * the browser over this window.
 */
const MAX_FETCHED_COMPANIES = 100;

/**
 * Contact statuses whose silence is worth flagging. `noteStatuses` carries only
 * `value` / `label` / `color` and its order is whatever the administrator typed
 * in Settings, so nothing in the configuration marks a status as "engaged" and
 * the two values cannot be derived from it. They are instead matched against
 * the configured list, so a status that has been removed is never queried.
 */
const ENGAGED_CONTACT_STATUSES = ["hot", "in-contract"];

type AttentionRow = {
  id: string;
  name: string;
  reason: string;
  to: string;
};

/**
 * A shortlist of exceptions, each row saying why it is on the list. Priority
 * order: engaged contacts that have gone quiet, then accounts with money in
 * play and nothing scheduled, then accounts nobody owns.
 */
export const NeedsAttention = () => {
  const translate = useTranslate();
  const { noteStatuses } = useConfigurationContext();
  const { salesId, isTeam, isPending: isScopePending } = useDashboardScope();

  const scopeFilter = useMemo(
    () => (isTeam ? {} : { sales_id: salesId }),
    [isTeam, salesId],
  );

  const engagedStatuses = useMemo(
    () =>
      noteStatuses
        .filter((status) => ENGAGED_CONTACT_STATUSES.includes(status.value))
        .map((status) => status.value),
    [noteStatuses],
  );

  const staleBefore = subDays(startOfToday(), STALE_CONTACT_DAYS).toISOString();

  // A configuration with none of the engaged statuses left simply drops this
  // bucket, rather than asking for `status in ()`.
  const isStaleQueryEnabled = !isScopePending && engagedStatuses.length > 0;

  const { data: staleContacts, isPending: isStalePending } =
    useGetList<Contact>(
      "contacts",
      {
        pagination: { page: 1, perPage: MAX_ROWS },
        sort: { field: "last_seen", order: "ASC" },
        filter: {
          "status@in": `(${engagedStatuses.join(",")})`,
          "last_seen@lt": staleBefore,
          ...scopeFilter,
        },
      },
      { enabled: isStaleQueryEnabled },
    );

  const { data: scopedCompanies, isPending: isCompaniesPending } =
    useGetList<Company>(
      "companies",
      {
        pagination: { page: 1, perPage: MAX_FETCHED_COMPANIES },
        sort: { field: "name", order: "ASC" },
        filter: scopeFilter,
      },
      { enabled: !isScopePending },
    );

  // An unowned account sits in nobody's scope, so it would be invisible on
  // every personal dashboard if this query carried the scope filter. It is
  // asked for on its own terms instead, in both views.
  const { data: unownedCompanies, isPending: isUnownedPending } =
    useGetList<Company>("companies", {
      pagination: { page: 1, perPage: MAX_ROWS },
      sort: { field: "name", order: "ASC" },
      filter: { "sales_id@is": null },
    });

  const rows = useMemo(() => {
    const staleRows: AttentionRow[] = (staleContacts ?? []).map((contact) => ({
      id: `contact:${contact.id}`,
      name: `${contact.first_name} ${contact.last_name}`,
      reason: translate("crm.dashboard.attention.reason_stale", {
        days: STALE_CONTACT_DAYS,
      }),
      to: `/contacts/${contact.id}/show`,
    }));

    const idleDealRows: AttentionRow[] = (scopedCompanies ?? [])
      .filter(
        (company) =>
          (company.open_deals_amount ?? 0) > 0 &&
          (company.nb_open_tasks ?? 0) === 0,
      )
      .sort((a, b) => (b.open_deals_amount ?? 0) - (a.open_deals_amount ?? 0))
      .map((company) => ({
        id: `company:${company.id}`,
        name: company.name,
        reason: translate("crm.dashboard.attention.reason_no_task"),
        to: `/companies/${company.id}/show`,
      }));

    const unownedRows: AttentionRow[] = (unownedCompanies ?? []).map(
      (company) => ({
        id: `company:${company.id}`,
        name: company.name,
        reason: translate("crm.dashboard.attention.reason_unowned"),
        to: `/companies/${company.id}/show`,
      }),
    );

    // An unowned account with an idle deal qualifies twice; the earlier, more
    // actionable reason wins and the row is listed once.
    const seenIds = new Set<string>();
    return [...staleRows, ...idleDealRows, ...unownedRows]
      .filter((row) => {
        if (seenIds.has(row.id)) return false;
        seenIds.add(row.id);
        return true;
      })
      .slice(0, MAX_ROWS);
  }, [scopedCompanies, staleContacts, translate, unownedCompanies]);

  if (isScopePending || isCompaniesPending || isUnownedPending) return null;
  if (isStaleQueryEnabled && isStalePending) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <TriangleAlert className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground flex-1">
          {translate("crm.dashboard.attention.title")}
        </h2>
      </div>
      <Card className="p-4 gap-2">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.dashboard.attention.empty")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {rows.map((row) => (
              <li key={row.id} className="py-2 first:pt-0 last:pb-0">
                <Link to={row.to} className="block hover:underline">
                  <span className="block text-sm font-medium truncate">
                    {row.name}
                  </span>
                  {/*
                    A list of exceptions is only useful if it names the
                    exception, so the reason travels with every row.
                  */}
                  <span className="block text-xs text-muted-foreground">
                    {row.reason}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};
